<template>
  <div
    v-if="!userAuthIsInitialLoading && userAuthDataId"
    class="h-full bg-white"
  >
    <AppHeader class="fixed top-0 h-header w-full z-2001" />
    <main class="flex flex-col h-full">
      <AppNavigation
        class="fixed top-header h-without-header w-sidebar hidden z-2000 tablet:block" />
      <AppContent
        class="flex-shrink-0 min-h-without-header mt-header tablet:min-h-without-header tablet:ml-sidebar" />
    </main>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import LogRocket from 'logrocket';
import { showSuccessNotification } from '@/utils/notification';
import { FBFirestoreRef } from '@/helpers/firebaseServices';
import AppHeader from '@/components/app/AppHeader';
import AppNavigation from '@/components/app/AppNavigation';
import AppContent from '@/components/app/AppContent';
import packagesModule from '@/store/modules/packages';
import navigationModule from '@/store/modules/navigation';
import userEventsModule from '@/store/modules/user/userEvents';
import userTeamMembersModule from '@/store/modules/user/userTeamMembers';
import userPackagesModule from '@/store/modules/user/userPackage';
import userPaymentsModule from '@/store/modules/user/userPayment';

// Notes: When changing this component it will throw VueX module errors in dev for the dynamically registered modules
export default {
  components: {
    AppHeader,
    AppNavigation,
    AppContent,
  },
  data: () => ({
    unsubPackages: null,
    unsubUser: null,
    unsubUserPackages: null,
    unsubUserPayments: null,
    // unsubscribeUserTeamMembers: null,
    unwatchLatestPayment: null,
  }),
  created() {
    // if (!this.$store.state.events) this.$store.registerModule('events', eventsModule);
    if (!this.$store.state.navigation) this.$store.registerModule('navigation', navigationModule);
    if (!this.$store.state.packages) this.$store.registerModule('packages', packagesModule);
    if (!this.$store.state.user.events) this.$store.registerModule(['user', 'events'], userEventsModule);
    if (!this.$store.state.user.teamMembers) this.$store.registerModule(['user', 'teamMembers'], userTeamMembersModule);
    if (!this.$store.state.user.package) this.$store.registerModule(['user', 'package'], userPackagesModule);
    if (!this.$store.state.user.payment) this.$store.registerModule(['user', 'payment'], userPaymentsModule);
  },
  watch: {
    userAuthIsInitialLoading: {
      immediate: true,
      handler(newValue) {
        if (newValue) return;
        if (!this.userAuthDataId) {
          // Redirect user to sign-in if user
          this.$router.replace({ name: 'AuthSignIn' });

          return;
        }

        // User is signed in
        this.unsubPackages = this.subPackages();
        this.unsubUser = this.subUser(this.userAuthDataId);
        this.unsubUserPackages = this.subUserPackages(this.userAuthDataId);
        this.unsubUserPayments = this.subUserPayments(this.userAuthDataId);
      },
    },
    '$route.query.paymentId': {
      immediate: true,
      handler(newValue) {
        if (!newValue || this.$store.state.user?.payment?.isInitialLoading) return;

        this.handlePaymentTracking();
      },
    },
    '$store.state.user.payment.isInitialLoading': { // Not using mapState because of error in console
      immediate: true,
      handler(newValue) {
        if (newValue || !this.$route.query.paymentId) return;

        this.handlePaymentTracking();
      },
    },
  },
  computed: {
    ...mapState('packages', {
      packageIsInitialLoading: 'isInitialLoading',
    }),
    ...mapState('user/auth', {
      userAuthIsInitialLoading: 'isInitialLoading',
    }),
    ...mapGetters('user/auth', {
      userAuthDataId: 'authDataId',
    }),
  },
  methods: {
    ...mapActions('packages', {
      onPackageSnapshot: 'onSnapshot',
    }),
    ...mapActions('user', {
      updateUserDetails: 'updateDetails',
    }),
    ...mapActions('user/package', {
      onUserPackageSnapshot: 'onSnapshot',
    }),
    ...mapActions('user/payment', {
      onUserPaymentSnapshot: 'onSnapshot',
    }),
    handlePaymentTracking() {
      // console.log();
      // Watcher to check when the paymentDoc status is fulfilled
      this.unwatchLatestPayment = this.$watch(() => {
        const latestPayment = this.$store.state?.user?.payment?.paymentDocs
          .find((paymentDoc) => paymentDoc.id === this.$route.query.paymentId);

        if (!latestPayment) return '';

        return latestPayment;
      }, (latestPayment) => {
        if (!latestPayment || latestPayment.fields.status !== 'fulfilled') return;

        this.trackPayment(latestPayment);
      }, { immediate: true, deep: false });
    },
    trackPayment(payment) {
      const { dataLayer } = window;

      // Showing that the payment was successful
      this.$router.replace({ name: this.$route.name, params: { ...this.$route.params } });

      if (payment.fields.type === 'oneOff') {
        showSuccessNotification('You have upgraded your Event successfully', 8000);
      } else {
        showSuccessNotification('You are now an Eventor  subscriber', 8000);
      }

      if (this.unwatchLatestPayment) this.unwatchLatestPayment();

      // Google Analytics ECommerce Tracking specification
      // # Event purchase (from free to any package)
      // Event: 'purchase-event'
      // Product name: 'Silver' event package
      // Product category: oneOff
      //
      // # Event upgrade event purchase 1 (from an existing package)
      // Event: 'purchase-event-upgrade'
      // Product name: 'Silver' <- 'Bronze' event upgrade
      // Product category: oneOff upgrade
      //
      // # Event upgrade event purchase 2 (from an existing subscription event package)
      // Event: 'purchase-event-subscription-upgrade'
      // Product name: 'Silver' <- 'Bronze subscription' event upgrade
      // Product category: subscriptionPlan event upgrade
      //
      // # Subscription purchase
      // Event: 'purchase-subscription'
      // Product name: 'Silver' Eventor subscription
      // Product category: subscriptionPlan
      //
      // TODO # Subscription upgrade purchase (from an existing subscription package)
      // Event: 'purchase-subscription-upgrade'
      // Product name: 'Silver subscription' <- 'Bronze subscription' event upgrade
      // Product category: subscriptionPlan upgrade

      let event = '';
      let productSKU = '';
      let productName = '';
      let productCategory = '';

      if (payment.fields.type === 'oneOff') {
        event = 'purchase-event';
        productSKU = payment.fields.packageId;
        productName = `${payment.fields.package.shortName} event package`;
        productCategory = payment.fields.type;
      } else if (payment.fields.type === 'subscriptionPlan') {
        event = 'purchase-subscription';
        productSKU = payment.fields.packageId;
        productName = `${payment.fields.package.shortName} Eventor subscription`;
        productCategory = payment.fields.type;
      } else {
        return;
      }

      if (payment.fields.subscriptionPlanPackage) {
        // Event was created with an existing subscription package and upgraded to a better package
        event += '-subscription-upgrade';
        productSKU = `${payment.fields.previousPackageId}(${payment.fields.subscriptionPlanPackageId})->${payment.fields.packageId}`;
        productName = `${payment.fields.package.shortName} <- ${payment.fields.subscriptionPlanPackage.shortName} event upgrade`;
        productCategory += ' event upgrade';
      } else if (payment.fields.previousPackage) {
        // Event upgrade
        event += '-upgrade';
        productSKU = `${payment.fields.previousPackageId}->${payment.fields.packageId}`;
        productName = `${payment.fields.package.shortName} <- ${payment.fields.previousPackage.shortName} event upgrade`;
        productCategory += ' upgrade';
      }

      // Don't track payments on development environments
      if (process.env.VUE_APP_MODE === 'development' || process.env.NODE_ENV === 'development') {
        console.log(payment);
        console.log({
          event,
          transactionId: payment.fields.stripeSessionId,
          transactionTotal: Number.parseFloat(payment.fields.amountPaid) / 100,
          transactionCurrency: payment.fields.currencyCode.toUpperCase(),
          transactionProducts: [{
            sku: productSKU,
            name: productName,
            price: Number.parseFloat(payment.fields.amountPaid) / 100,
            category: productCategory,
            quantity: 1,
          }],
        });

        return;
      }

      dataLayer.push({
        event,
        transactionId: payment.fields.stripeSessionId,
        transactionTotal: Number.parseFloat(payment.fields.amountPaid) / 100,
        transactionCurrency: payment.fields.currencyCode.toUpperCase(),
        transactionProducts: [{
          sku: productSKU,
          name: productName,
          price: Number.parseFloat(payment.fields.amountPaid) / 100,
          category: productCategory,
          quantity: 1,
        }],
      });
    },
    subPackages() {
      return FBFirestoreRef.collection('packages')
        .where('isActive', '==', true)
        .orderBy('pricings.usd.value', 'asc')
        .onSnapshot((snapshot) => {
          this.onPackageSnapshot(snapshot);
        }, (error) => {
          LogRocket.captureException(error);
          console.error(error);
        });
    },
    subUser(userId) {
      return FBFirestoreRef.collection('users')
        .doc(userId)
        .onSnapshot((doc) => {
          if (!doc.exists) return;

          this.updateUserDetails(doc.data());
        }, (error) => {
          LogRocket.captureException(error);
          console.error(error);
        });
    },
    subUserPackages(userId) {
      return FBFirestoreRef.collection('users')
        .doc(userId)
        .collection('packages')
        .where('isActive', '==', true)
        .onSnapshot((snapshot) => {
          this.onUserPackageSnapshot(snapshot);
        }, (error) => {
          LogRocket.captureException(error);
          console.error(error);
        });
    },
    subUserPayments(userId) {
      return FBFirestoreRef.collection('users')
        .doc(userId)
        .collection('payments')
        .where('status', '==', 'fulfilled')
        .onSnapshot((snapshot) => {
          this.onUserPaymentSnapshot(snapshot);
        }, (error) => {
          LogRocket.captureException(error);
          console.error(error);
        });
    },
  },
  beforeDestroy() {
    // Unsubscribe all unnecessary realtime listeners
    if (this.unsubPackages !== null) this.unsubPackages();
    if (this.unsubUser !== null) this.unsubUser();
    if (this.unsubUserPackages !== null) this.unsubUserPackages();
    if (this.unsubUserPayments !== null) this.unsubUserPayments();

    // Unregister VueX modules that aren't accessible when not logged in
    if (this.$store.state.navigation) this.$store.unregisterModule('navigation');
    if (this.$store.state.packages) this.$store.unregisterModule('packages');
    if (this.$store.state.user.events) this.$store.unregisterModule(['user', 'events']);
    if (this.$store.state.user.teamMembers) this.$store.unregisterModule(['user', 'teamMembers']);
    if (this.$store.state.user.package) this.$store.unregisterModule(['user', 'package']);
    if (this.$store.state.user.payment) this.$store.unregisterModule(['user', 'payment']);
  },
};
</script>

<style>
.intercom-lightweight-app .intercom-lightweight-app-launcher {
  bottom: 100px;
}
</style>
